import { useLocalStorage } from "usehooks-ts";
import { useEvents } from "../../hooks";
import { useMarketStorefrontSessionId } from "../../hooks/useMarketStorefrontSessionTracking";
import { useConsolidatedOrderingIntercom } from "../../hooks/useConsolidatedOrderingIntercom";
import { useUnifiedCheckout } from "./UnifiedCheckoutProvider.shared";

export type UnifiedCheckoutStep = "overview" | "details" | "pay" | "success";
export type UnifiedCheckoutResult = "completed" | "partial" | "failed";

const TRACKING_STORAGE_KEY = "consolidated-ordering-tracking";

type StoredTrackingState = {
  emittedKeys: string[];
  basketSnapshots?: Record<string, { supplierCount: number; skuCount: number }>;
};

export const useUnifiedCheckoutTracking = () => {
  const { track } = useEvents();
  const { trackCheckoutStep, trackCompleted, updateJourney } =
    useConsolidatedOrderingIntercom();
  const { sessionId, journeySource } = useMarketStorefrontSessionId();
  const { sdBasket, sdOrders } = useUnifiedCheckout();
  const [trackingState, setTrackingState] =
    useLocalStorage<StoredTrackingState>(TRACKING_STORAGE_KEY, {
      emittedKeys: [],
    });

  const emitOnce = (key: string, emit: () => void): void => {
    if (trackingState.emittedKeys.includes(key)) return;

    emit();
    setTrackingState((current: StoredTrackingState) => ({
      emittedKeys: [...current.emittedKeys.slice(-199), key],
      basketSnapshots: current.basketSnapshots,
    }));
  };

  const currentSupplierIds = [
    ...new Set(
      [
        ...sdOrders.map((order) => order.SupplierId),
        ...(sdBasket.paidSupplierIds ?? []),
      ].filter((supplierId): supplierId is number => supplierId != null),
    ),
  ];
  const currentSkuCount = sdOrders.reduce(
    (count, order) => count + (order.OrderItems?.length ?? 0),
    0,
  );
  const getTrackingProperties = () => {
    const previousSnapshot = trackingState.basketSnapshots?.[sdBasket.uuid];
    const supplierCount = Math.max(
      currentSupplierIds.length,
      previousSnapshot?.supplierCount ?? 0,
    );
    const skuCount = Math.max(currentSkuCount, previousSnapshot?.skuCount ?? 0);

    setTrackingState((current: StoredTrackingState) => ({
      ...current,
      basketSnapshots: {
        ...current.basketSnapshots,
        [sdBasket.uuid]: { supplierCount, skuCount },
      },
    }));

    return {
      supplierCount,
      sharedProperties: {
        ...(sessionId ? { sessionId } : {}),
        journeySource,
        sdBasketUuid: sdBasket.uuid,
        supplierCount,
        ...(skuCount > 0 ? { skuCount } : {}),
        isConsolidatedBasket: supplierCount >= 2,
      },
    };
  };

  const getIntercomSnapshot = (supplierCount: number) => ({
    ...(sessionId ? { sessionId } : {}),
    journeySource,
    supplierCount,
    itemCount: sdOrders.reduce(
      (count, order) =>
        count +
        (order.OrderItems ?? []).reduce(
          (orderCount, item) => orderCount + item.quantity,
          0,
        ),
      0,
    ),
    paidSupplierCount: Math.max(
      (sdBasket.paidSupplierIds?.length ?? 0) -
        (sdBasket.failedSupplierIds?.length ?? 0),
      0,
    ),
    failedSupplierCount: sdBasket.failedSupplierIds?.length ?? 0,
  });

  const trackStepViewed = (step: UnifiedCheckoutStep) => {
    const { sharedProperties, supplierCount } = getTrackingProperties();
    const intercomSnapshot = getIntercomSnapshot(supplierCount);

    emitOnce(`${sdBasket.uuid}:step:${step}`, () => {
      track("Unified Checkout Step Viewed", {
        ...sharedProperties,
        step,
        ...(sdBasket.deliveryPreference
          ? { deliveryPreference: sdBasket.deliveryPreference }
          : {}),
      });
    });
    trackCheckoutStep({
      journeyKey: sdBasket.uuid,
      step,
      snapshot: intercomSnapshot,
      ...(sdBasket.deliveryPreference
        ? { deliveryPreference: sdBasket.deliveryPreference }
        : {}),
    });
    if (step === "pay" && (sdBasket.failedSupplierIds?.length ?? 0) > 0) {
      updateJourney({
        journeyKey: sdBasket.uuid,
        status: "partially_completed",
        step: "pay",
        snapshot: intercomSnapshot,
      });
    }

    if (step === "success") {
      emitOnce(`${sdBasket.uuid}:result:completed`, () => {
        track("Unified Checkout Result", {
          ...sharedProperties,
          result: "completed" satisfies UnifiedCheckoutResult,
          orderCount: sdBasket.paidOrderIds?.length ?? 0,
          completedSupplierCount: sdBasket.paidSupplierIds?.length ?? 0,
          remainingSupplierCount: 0,
          paymentState:
            (sdBasket.pendingSupplierIds?.length ?? 0) > 0
              ? "pending"
              : "succeeded",
        });
      });
      trackCompleted({
        journeyKey: sdBasket.uuid,
        snapshot: intercomSnapshot,
        orderCount: sdBasket.paidOrderIds?.length ?? 0,
        ...(sdBasket.deliveryPreference
          ? { deliveryPreference: sdBasket.deliveryPreference }
          : {}),
      });
    }
  };

  const trackResult = ({
    result,
    failureStage,
    errorCategory,
  }: {
    result: Exclude<UnifiedCheckoutResult, "completed">;
    failureStage: "order-creation" | "payment";
    errorCategory?: string;
  }) => {
    const { sharedProperties, supplierCount } = getTrackingProperties();
    const intercomSnapshot = getIntercomSnapshot(supplierCount);
    const completedSupplierCount = sdBasket.paidSupplierIds?.length ?? 0;
    track("Unified Checkout Result", {
      ...sharedProperties,
      result,
      orderCount: sdBasket.paidOrderIds?.length ?? 0,
      completedSupplierCount,
      remainingSupplierCount: Math.max(
        supplierCount - completedSupplierCount,
        0,
      ),
      paymentState:
        completedSupplierCount > 0
          ? (sdBasket.pendingSupplierIds?.length ?? 0) > 0
            ? "mixed"
            : "succeeded"
          : "failed",
      failureStage,
      ...(errorCategory ? { errorCategory } : {}),
    });
    updateJourney({
      journeyKey: sdBasket.uuid,
      status: result === "partial" ? "partially_completed" : "checkout",
      step: "pay",
      snapshot: intercomSnapshot,
    });
  };

  return {
    trackStepViewed,
    trackResult,
  };
};
